import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroup, fetchStream, fetchStreams } from "actions/streams";
import useMeasure from "react-use-measure";
import Text from "components/text";
import { useSpring, animated, useTransition } from "react-spring";
import InfiniteScroll from "react-infinite-scroller";
import dateformat from "dateformat";
import FileService from "services/file.service";
import { TabBar } from "components/tabbar";
import {
  fetchCollection,
  fetchCollections,
  fetchCollectionSection,
} from "actions/collections";

import styles from "./entityBrowser.module.scss";
import cx from "classnames";
import _ from "lodash";
import { REDIRECT_URL } from "constants/actionTypes";

const DefaultAsset = "/images/default_asset.jpg";
const webAppUrl = process.env.REACT_APP_WEBAPP_URL;

export const EntityBrowser = ({
  className,
  width,
  items,
  action,
  onSelection,
  loading,
  attachAssets,
  generateLinkForAsset
}) => {
  return (
    <div className={cx(styles.container, className)}>
      {items &&
        items.map((item) => (
          <EntityRow
            key={item.id}
            entity={item}
            width={width}
            action={action}
            onSelection={onSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        ))}
      {items?.length === 0 && !loading && (
        <div className={styles.empty}>No items found</div>
      )}
    </div>
  );
}

function EntityRow({ entity, width, action, onSelection, attachAssets, generateLinkForAsset }) {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case "stream":
        return StreamRow;
      case "group":
        return GroupRow;
      case "collection":
        return CollectionRow;
      case "collection_section":
        return CollectionSectionRow;
      case "asset":
        return AssetRow;
      default:
        return false;
    }
  }, [entity]);

  if (!Component) {
    return null;
  }

  return React.createElement(Component, { entity, width, action, onSelection, attachAssets, generateLinkForAsset });
}

function StreamRow({ entity, width, action, onSelection, attachAssets, generateLinkForAsset }) {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const childrenAnimation = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  });

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, {
          [styles.expanded]: expanded,
        })}
         style={{ width: `${width}%` }}
        onClick={handleClick}
      >
        <Text
          text={entity?.name || ""}
          lines={1}
          className={styles.name}
          tooltip={{ placement: "auto" }}
        />
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <StreamBrowser
              entity={entity}
              width={width}
              action={action}
              onSelection={onSelection}
              attachAssets={attachAssets}
              generateLinkForAsset={generateLinkForAsset}
            />
          </div>
        </animated.div>
      )}
    </>
  );
}

const CollectionRow = ({ entity, width, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const actionComponent = useMemo(() => {
    return action && action(entity);
  }, [action]);

  const childrenAnimation = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  });

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
    if (!action) {
      onSelection(entity);
    }
  };

  const handleActionClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onSelection(entity);
  };

  const handleGenerateLinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSelection(entity);
  }

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, styles.collection, {
          [styles.expanded]: expanded,
        })}
        style={{ width: `${width}%` }}
        onClick={handleClick}
      >
        <div className={styles.inner}>
          <Text
            text={entity?.name || ""}
            lines={1}
            className={styles.name}
            tooltip={{ placement: "auto" }}
          />

          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>

          {/* {!attachAssets && (<button className={styles.action} onClick={handleGenerateLinkClick}>
            <i className={cx({
              "paperflite-link": !attachAssets,
            })}></i>
          </button>)} */}
          
        </div>
        <div
          className={cx(styles.actions, {
            [styles.hasActions]: actionComponent,
          })}
        >
          <div className={styles.action} onClick={handleActionClick}>
            {actionComponent}
          </div>

          <span className={styles.attachContainer}>
            <button className={cx(styles.action, styles.attachAction, {
            })} onClick={handleGenerateLinkClick}>
              <i className={cx(styles.attachIcon, {
                "paperflite-link": !attachAssets,
                "paperflite-cursor": attachAssets,
              })}></i>
            </button>
            <span className={styles.count}>
            {entity?.sectionsCount > 0
              ? `${entity?.sectionsCount} Sections`
              : ""}
            {entity?.sectionsCount === 0 ? `${entity?.assetsCount} Assets` : ""}
            </span>
          </span>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionBrowser
              entity={entity}
              width={width}
              action={action}
              onSelection={onSelection}
              attachAssets={attachAssets}
              generateLinkForAsset={generateLinkForAsset}
            />
          </div>
        </animated.div>
      )}
    </>
  );
};

const CollectionSectionRow = ({ entity, width, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const childrenAnimation = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  });

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, styles.collection)}
         style={{ width: `${width}%` }}
        onClick={handleClick}
      >
        <Text
          className={styles.name}
          text={entity?.name || ""}
          lines={1}
          tooltip={{ placement: "auto" }}
        />
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionSectionBrowser
              entity={entity}
              width={width}
              action={action}
              onSelection={onSelection}
              attachAssets={attachAssets}
              generateLinkForAsset={generateLinkForAsset}
            />
          </div>
        </animated.div>
      )}
    </>
  );
};

export const StreamsBrowser = ({ className, width, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const dispatch = useDispatch();
  const { loading, streams } = useSelector((state) => {
    return {
      loading: state.streams.loading,
      streams: state.streams?.items,
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchStreams());
  }, []);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      {loading && _.size(streams) === 0 && (
        <>
          <StreamPlaceholder width={"50%"} />
          <StreamPlaceholder width={"70%"} />
          <StreamPlaceholder width={"60%"} />
          <StreamPlaceholder width={"50%"} />
        </>
      )}
      <EntityBrowser
        className={className}
        items={streams}
        width={width}
        action={action}
        onSelection={onSelection}
        loading={loading}
        attachAssets={attachAssets}
        generateLinkForAsset={generateLinkForAsset}
      />
    </div>
  );
};

export const CollectionsBrowser = ({ className, width, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector(
    (state) => {
      const collectionState = _.get(state, "collections");
      const loading = _.get(collectionState, "loading");
      const pagination = _.get(collectionState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = pagination.current + 1 < total;

      const collections = _.get(collectionState, "items") || [];

      let nextPage = _.get(pagination, "current", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        collections: collections,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual
  );

  useEffect(() => {
    fetchCollectionsForPage(0);
  }, []);

  const fetchCollectionsForPage = (page) => {
    dispatch(fetchCollections(page, 20));
  };

  const handleLoadMoreCollections = () => {
    if (nextPage && !loading) {
      fetchCollectionsForPage(nextPage);
    }
  };

  const filteredCollections = collections.filter((collection) => collection.id !== "uncategorized" && !collection.internal && !collection.dealRoom);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(filteredCollections) === 0) {
            return (
              <>
                <CollectionPlaceholder width={"50%"} />
                <CollectionPlaceholder width={"70%"} />
                <CollectionPlaceholder width={"60%"} />
                <CollectionPlaceholder width={"50%"} />
              </>
            );
          } else {
            return <CollectionPlaceholder width={"70%"} />;
          }
        }}
      >
        <EntityBrowser
          className={className}
          width={width}
          items={filteredCollections}
          action={action}
          onSelection={onSelection}
          loading={loading}
          attachAssets={attachAssets}
          generateLinkForAsset={generateLinkForAsset}
        />
      </InfiniteScroll>
    </div>
  );
};

const CollectionBrowser = ({
  className,
  width,
  entity,
  action,
  onSelection,
  attachAssets,
  generateLinkForAsset
}) => {
  const dispatch = useDispatch();
  const collection = entity;
  const childrenType =
    collection.sectionsCount > 0 ? "collection_section" : "asset";

  const { loading, items } = useSelector((state) => {
    if (!entity) {
      return {
        loading: false,
        items: [],
      };
    }

    const collection = state.collections.collection[entity?.id]?.item;
    let items = [];
    if (collection) {
      if (_.size(collection.sections) > 0) {
        items = (collection.sections || []).map((item) => {
          return {
            ...item,
            entityType: "collection_section",
          };
        });
      } else if (_.size(collection.assets) > 0) {
        items = (collection.assets || []).map((item) => {
          return {
            ...item,
          };
        });
      }
    }

    return {
      loading: state.collections.collection[entity?.id]?.loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchCollection(entity.id));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      {childrenType === "collection_section" && loading && (
        <>
          <CollectionSectionPlaceholder width="50%" />
          <CollectionSectionPlaceholder width="70%" />
          <CollectionSectionPlaceholder width="60%" />
          <CollectionSectionPlaceholder width="50%" />
        </>
      )}

      {childrenType === "asset" && loading && (
        <>
          {_.times(collection.assetsCount || 3, (index) => (
            <AssetPlaceholder key={index} />
          ))}
        </>
      )}

      <EntityBrowser
        className={className}
        items={items}
        width={width}
        action={action}
        onSelection={onSelection}
        loading={loading}
        attachAssets={attachAssets}
        generateLinkForAsset={generateLinkForAsset}
      />
    </div>
  );
};

const CollectionSectionBrowser = ({
  className,
  width,
  entity,
  action,
  onSelection,
  attachAssets,
  generateLinkForAsset
}) => {
  const dispatch = useDispatch();
  const collectionSection = entity;

  const { loading, items } = useSelector((state) => {
    if (!entity) {
      return {
        loading: false,
        items: [],
      };
    }

    const collectionState =
      state.collections.collection[collectionSection?.collectionId]?.item;
    if (!collectionState) {
      return {
        loading: false,
        items: [],
      };
    }

    let items = [];
    let loading = false;
    const sectionState = collectionState.sections.find(
      (section) => section.id === collectionSection.id
    );

    if (sectionState) {
      items = (sectionState.assets || []).map((item) => item);
      loading = sectionState.loading;
    }

    return {
      loading: loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(
        fetchCollectionSection(collectionSection.collectionId, entity.id)
      );
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      {loading && (
        <>
          {_.times(3, (index) => (
            <AssetPlaceholder key={index} />
          ))}
        </>
      )}

      <EntityBrowser
        className={className}
        items={items}
        width={width}
        action={action}
        onSelection={onSelection}
        loading={loading}
        attachAssets={attachAssets}
        generateLinkForAsset={generateLinkForAsset}
      />
    </div>
  );
};

const StreamBrowser = ({ className, width, entity, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state) => {
    if (!entity) {
      return {
        loading: false,
        items: [],
      };
    }

    return {
      loading: _.get(state.streams, `stream.${entity?.id}.loading`, false),
      items:
        _.get(state.streams, `stream.${entity?.id}.item.children`, []) || [],
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchStream(entity.id));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        loading={loading}
        hasMore={loading}
        loadMore={() => {}}
        loader={
          <>
            {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
              <AssetPlaceholder key={index} />
            ))}
          </>
        }
      >
        <EntityBrowser
          className={className}
          width={width}
          items={items}
          action={action}
          onSelection={onSelection}
          loading={loading}
          attachAssets={attachAssets}
          generateLinkForAsset={generateLinkForAsset}
        />
      </InfiniteScroll>
    </div>
  );
};

const GroupBrowser = ({ className, width, entity, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state) => {
    if (!entity) {
      return {
        loading: false,
        items: [],
      };
    }

    return {
      loading: _.get(state.streams, `group.${entity?.id}.loading`, false),
      items:
        _.get(state.streams, `group.${entity?.id}.item.children`, []) || [],
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchGroup(entity.id));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        loading={loading}
        hasMore={loading}
        loadMore={() => {}}
        loader={
          <>
            {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
              <AssetPlaceholder key={index} />
            ))}
          </>
        }
      >
        <EntityBrowser
          className={className}
          width={width}
          items={items}
          action={action}
          onSelection={onSelection}
          loading={loading}
          attachAssets={attachAssets}
          generateLinkForAsset={generateLinkForAsset}
        />
      </InfiniteScroll>
    </div>
  );
};

const GroupRow = ({ entity, width, action, onSelection, attachAssets, generateLinkForAsset }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const childrenAnimation = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  });

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  };

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, {
          [styles.expanded]: expanded,
        })}
        style={{ width: `${width}%` }}
        onClick={handleClick}
      >
        <div className={styles.name}>
          <Text text={entity?.name || ""} tooltip={{ placement: "auto" }} />
        </div>
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
      </div>
      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <animated.div style={childrenAnimation} className={styles.children}>
            <div ref={containerRef} className={styles.inner}>
              <GroupBrowser
                entity={entity}
                width={width}
                action={action}
                onSelection={onSelection}
                attachAssets={attachAssets}
                generateLinkForAsset={generateLinkForAsset}
              />
            </div>
          </animated.div>
        )}
      </animated.div>
    </>
  );
};

const AssetRow = ({ entity, width, action, onSelection, attachAssets, generateLinkForAsset }) => {

  const selectedAssets = useSelector((state) => _.get(state, "selection.assets", []));
  const activeTab = useSelector((state) => _.get(state, "application.activeTab"));

  const [showTooltip, setShowTooltip] = React.useState(false);
  const [isPaperflite, setIsPaperflite] = React.useState(false);

  useEffect(() => {
    if (activeTab && activeTab.includes("app.paperflite.com")) {
      setIsPaperflite(true);
    }
  }, [activeTab]);

  const fileType = useMemo(() => {
    return FileService.fileType(entity.metadata);
  }, [entity]);

  const updatedDate = _.get(entity, "contentModifiedDate");

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (onSelection) {
      onSelection(entity);
    }
  };

  const generateLinkForAssetClicked = (event) => {
    event.preventDefault();
    event.stopPropagation();
    generateLinkForAsset(entity);
  }

  const triggerMessageToPlugin = (message) => {
    window.parent.postMessage(message, "*");
  }

  const redirectToAsset = () => {
    if(isPaperflite){
        triggerMessageToPlugin({
          type: REDIRECT_URL,
          payload: `${webAppUrl}/assets/${entity.id}?source=chrome_extension`,
        });
      }
  }

  const isSelected = selectedAssets
    ? selectedAssets.some((asset) => _.get(asset, "id") === _.get(entity, "id"))
    : false;

  const isInternal = !entity?.settings?.share;

  return (
    <a
      href={isPaperflite ? null : `${webAppUrl}/assets/${entity.id}?source=chrome_extension`}
      target="_blank"
      className={cx(styles.entity, styles.asset, {
        [styles.selectedEntity]: isSelected,
      })}
      style={{ width: `${width}%` }}
      onClick={redirectToAsset}
    >
     <i className={cx({
      [styles.selectIcon]: !isSelected,
      [styles.selectedIcon]: isSelected,
      [styles.hideSelectIcon]: !attachAssets,
      "paperflite-tick-selected": isSelected && !isInternal,
      "paperflite-radio-button-unselected": !isSelected && !isInternal,
      "paperflite-lock": attachAssets && isInternal
      })}
      onClick={(event) => handleClick(event)}
      />
      <div className={styles.inner}>
        <img
          alt="asset thumbnail"
          src={entity?.icon?.full || DefaultAsset}
          className={styles.icon}
        />
        <div className={styles.details}>
          <Text
            text={entity?.name || ""}
            lines={1}
            className={styles.name}
            tooltip={{ placement: "auto" }}
            width={90}
          />
          <div className={styles.meta}>
            <span>{fileType}{" "}</span>
            <span className={styles.separator} />
            <span>{updatedDate &&
              `Updated ${dateformat(updatedDate, "dd mmm yyyy")}`}
            </span>
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        {action && <div className={styles.action}>{action(entity)}</div>}
        {!attachAssets && (
          <span className={styles.attachContainer}>
          <button className={cx(styles.action, styles.attachAction)} onClick={(event) => {
            generateLinkForAssetClicked(event);
          }}>
            <i className={cx({
              "paperflite-link": !attachAssets,
            })}></i>
          </button>
          </span>
        )}
      </div>
    </a>
  );
};

export const StreamPlaceholder = ({ width }) => {
  return (
    <div
      className={cx(styles.entity, styles.placeholder)}
      style={{ width: width }}
    >
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
};

export const CollectionPlaceholder = ({ width }) => {
  return (
    <div
      className={cx(styles.entity, styles.placeholder)}
      style={{ width: width }}
    >
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
};

export const CollectionSectionPlaceholder = ({ width }) => {
  return (
    <div
      className={cx(styles.entity, styles.placeholder)}
      style={{ width: width }}
    >
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
};

export const AssetPlaceholder = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} /> |{" "}
            <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
};